// src/pages/Expenses/parts/types.ts
//
// Shared types + mappers for the Expenses admin screens.
// Mirrors enrichedExpense in adminControllers/expense.go.

export type ExpenseStatus = 'pending' | 'approved' | 'rejected';

// Raw row exactly as the API returns it (GET /expenses | /expenses/:id)
export interface ApiExpense {
  id: string;
  category: string;
  payee_name: string;
  payee_contact: string;
  amount: number;
  payment_mode: string;
  transaction_ref: string;
  expense_date: string;
  description: string;
  receipt_url: string;
  status: ExpenseStatus;
  rejection_reason: string;
  recorded_by_name: string;
  approved_by_name: string;
  created_at: string;
}

// camelCase shape used by the components
export interface ExpenseRecord {
  id: string;
  category: string;
  payeeName: string;
  payeeContact: string;
  amount: number;
  paymentMode: string;
  transactionRef: string;
  expenseDate: string;
  description: string;
  receiptUrl: string;
  status: ExpenseStatus;
  rejectionReason: string;
  recordedByName: string;
  approvedByName: string;
  createdAt: string;
}

export interface ExpensePagination {
  current_page: number;
  per_page: number;
  total_items: number;
  total_pages: number;
}

export type ExpenseStatusCounts = Record<ExpenseStatus, number>;

export interface ExpenseSummaryRow {
  category: string;
  total_amount: number;
  count: number;
}

export const mapApiExpense = (row: ApiExpense): ExpenseRecord => ({
  id: row.id,
  category: row.category || '',
  payeeName: row.payee_name || '',
  payeeContact: row.payee_contact || '',
  amount: row.amount ?? 0,
  paymentMode: row.payment_mode || '',
  transactionRef: row.transaction_ref || '',
  expenseDate: row.expense_date || '',
  description: row.description || '',
  receiptUrl: row.receipt_url || '',
  status: row.status,
  rejectionReason: row.rejection_reason || '',
  recordedByName: row.recorded_by_name || '',
  approvedByName: row.approved_by_name || '',
  createdAt: row.created_at || '',
});

export const STATUS_META: Record<
  ExpenseStatus,
  { label: string; color: string; bg: string; border: string; icon: string }
> = {
  pending: {
    label: 'Pending',
    color: '#b45309',
    bg: 'rgba(245,158,11,.10)',
    border: 'rgba(245,158,11,.25)',
    icon: 'pi pi-clock',
  },
  approved: {
    label: 'Approved',
    color: '#15803d',
    bg: 'rgba(22,163,74,.10)',
    border: 'rgba(22,163,74,.25)',
    icon: 'pi pi-check-circle',
  },
  rejected: {
    label: 'Rejected',
    color: '#b91c1c',
    bg: 'rgba(239,68,68,.10)',
    border: 'rgba(239,68,68,.25)',
    icon: 'pi pi-times-circle',
  },
};

// Category is a free-text field on the backend (plain string filter/match),
// not an enum — these are quick-pick suggestions, the form still accepts
// any typed category.
export const CATEGORY_SUGGESTIONS = [
  'Salaries',
  'Utilities',
  'Maintenance',
  'Supplies',
  'Transport',
  'Events',
  'Miscellaneous',
];

// Value must be exactly "cash" — the backend's non-cash check
// (`paymentMode != "cash"`) is a literal string comparison.
export const PAYMENT_MODE_OPTIONS: { label: string; value: string }[] = [
  { label: 'Cash', value: 'cash' },
  { label: 'Cheque', value: 'cheque' },
  { label: 'Bank Transfer', value: 'bank_transfer' },
  { label: 'UPI', value: 'upi' },
  { label: 'Card', value: 'card' },
  { label: 'Other', value: 'other' },
];

export const paymentModeLabel = (value: string): string =>
  PAYMENT_MODE_OPTIONS.find((o) => o.value === value)?.label || value || '—';

export const formatCurrency = (amount: number): string =>
  `₹${amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

// expense_date is a plain "YYYY-MM-DD" string (not a Go time.Time), so it
// needs the T00:00:00 anchor to avoid a UTC-shift off-by-one.
export const formatExpenseDate = (dateStr: string): string => {
  if (!dateStr) return '—';
  const d = new Date(`${dateStr}T00:00:00`);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const formatDateTime = (iso: string): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

// Local YYYY-MM-DD — avoids the UTC shift from toISOString() for anyone
// in a timezone ahead of UTC.
export const toDateParam = (d: Date | null): string => {
  if (!d) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const isReceiptImage = (url: string): boolean =>
  /\.(png|jpe?g|gif|webp)(\?|$)/i.test(url);
